import React from "react";
import { Card, Flex, Select, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { LateralModal } from "@/components/modals/LateralModal";
import { ProductParticipantsPanel } from "./ProductParticipantsDrawer";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const { Text } = Typography;

interface Props {
  /** El `entity_id` de Magento. `null` en un alta o un borrador que nunca se publicó. */
  productId?: number | null;
  /**
   * Quien creó el borrador. Ausente en un alta: ahí el que crea es quien está mirando.
   */
  autorUserId?: number | null;
  /** Se avisa al padre cuando cambió algo en los participantes. */
  onChanged?: () => void;
}

/**
 * "Permisos" + candado, arriba a la derecha: el acceso a los participantes del producto,
 * **en cualquier estado** (xcons-docs#404). El mismo `LateralModal` que Permisos del
 * Catálogo, Presupuestos y Pedidos, y como ellos **sin el SKU en el encabezado**
 * (xcons-docs#409).
 */
export const PermisosDelProducto: React.FC<Props> = ({
  productId,
  autorUserId,
  onChanged,
}) => (
  <div style={{ flexShrink: 0 }}>
    <LateralModal
      title="Permisos"
      buttonSize="small"
      buttonType="text"
      buttonName="Permisos"
      iconPosition="end"
      iconSvg={<LockOutlined />}
    >
      {productId ? (
        <ProductParticipantsPanel productId={productId} onChanged={onChanged} />
      ) : (
        <DuenoSinPublicar autorUserId={autorUserId} />
      )}
    </LateralModal>
  </div>
);

/**
 * **Antes de publicarse no hay dónde guardar participantes**: la participación se guarda
 * sobre el producto de Magento, y un alta o un borrador todavía no lo tienen. Pero el
 * producto ya tiene dueño —quien lo creó— y el panel lo muestra, de sólo lectura.
 *
 * El borrador guarda el `authorUserId`, no el nombre: si el autor es quien mira, el nombre
 * sale de su sesión; si es otro, hasta que la API lo mande (xcons-docs#409, Notas) la fila
 * lo nombra por su rol.
 */
const DuenoSinPublicar: React.FC<{ autorUserId?: number | null }> = ({
  autorUserId,
}) => {
  const { data: yo } = useGetIdentity<{ id: number; name?: string }>();
  const esMio =
    autorUserId == null || (yo?.id != null && yo.id === autorUserId);
  const nombre = esMio && yo?.name ? yo.name : "Quien lo creó";

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      <Card
        className="drag-list-card"
        title={<Text strong>Participantes</Text>}
      >
        <Flex
          align="center"
          justify="space-between"
          gap={8}
          style={{ padding: "5px 12px" }}
        >
          <Flex align="center" gap={8} wrap="wrap">
            <Text strong style={{ fontSize: 12 }}>
              {nombre}
            </Text>
            <Text type="secondary">(Dueño)</Text>
          </Flex>
          <Select<string>
            size="small"
            style={{ width: 150, flexShrink: 0 }}
            value="admin"
            disabled
            options={[{ value: "admin", label: NIVEL.admin }]}
          />
        </Flex>
      </Card>
    </div>
  );
};
